import React, { useState } from 'react';
import { View } from 'react-native';
import { CalcShell, FormulaLine, FormulaNote, NumericField } from '../../../components/shell';
import { DiagramFumehoodFaceArea } from '../../../components/diagrams';
import { usePersistedState } from '../../../hooks/usePersistedState';
import { useUnits } from '../../../context/UnitsContext';

const KEY = 'tabai.calc.fumehoodFaceArea.v1';

type Fields = { sashHeight: string; sashWidth: string; faceArea: string };
const EMPTY: Fields = { sashHeight: '', sashWidth: '', faceArea: '' };

/**
 * Fumehood Face Area — A = H × W.
 * Sash H/W are in feet (length). Area in ft² (area).
 */
const FumehoodFaceAreaScreen = () => {
  const [fields, setFields, { clear: clearPersisted }] = usePersistedState<Fields>(KEY, EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [solvedFor, setSolvedFor] = useState<keyof Fields | null>(null);
  const { unit, toImp, fromImp } = useUnits();

  const set = (patch: Partial<Fields>) => setFields((prev) => ({ ...prev, ...patch }));

  const calculate = () => {
    setError(null);
    const filled = (Object.values(fields) as string[]).filter((v) => v !== '').length;
    if (filled < 2) {
      setError('Fill at least 2 of the 3 fields to solve for the missing one.');
      return;
    }
    if (filled === 3) {
      setError('All 3 values are filled — clear one to solve for it.');
      return;
    }

    const h = toImp(fields.sashHeight, 'length');
    const w = toImp(fields.sashWidth, 'length');
    const a = toImp(fields.faceArea, 'area');

    if (fields.faceArea === '') {
      if (!Number.isFinite(h) || !Number.isFinite(w) || h <= 0 || w <= 0) {
        setError('Sash height and width must be greater than zero.');
        return;
      }
      setSolvedFor('faceArea');
      set({ faceArea: fromImp(h * w, 'area', 2) });
    } else if (fields.sashHeight === '') {
      if (!Number.isFinite(a) || !Number.isFinite(w) || w <= 0) {
        setError('Sash width must be greater than zero.');
        return;
      }
      setSolvedFor('sashHeight');
      set({ sashHeight: fromImp(a / w, 'length', 2) });
    } else if (fields.sashWidth === '') {
      if (!Number.isFinite(a) || !Number.isFinite(h) || h <= 0) {
        setError('Sash height must be greater than zero.');
        return;
      }
      setSolvedFor('sashWidth');
      set({ sashWidth: fromImp(a / h, 'length', 2) });
    }
  };

  const clearAll = () => {
    setError(null);
    setSolvedFor(null);
    void clearPersisted();
  };

  const result = [
    fields.sashHeight && { label: 'Sash Height', value: fields.sashHeight, unit: unit('length'), emphasis: solvedFor === 'sashHeight' },
    fields.sashWidth && { label: 'Sash Width', value: fields.sashWidth, unit: unit('length'), emphasis: solvedFor === 'sashWidth' },
    fields.faceArea && { label: 'Face Area', value: fields.faceArea, unit: unit('area'), emphasis: solvedFor === 'faceArea' },
  ].filter(Boolean) as { label: string; value: string; unit?: string; emphasis?: boolean }[];

  return (
    <CalcShell
      title="Face Area"
      subtitle="Calculate face opening area using Face Area = Sash Height × Sash Width"
      formula={
        <>
          <FormulaLine>A = H × W</FormulaLine>
          <FormulaNote>
            A = Face Area ({unit('area')}), H = Sash Height ({unit('length')}), W = Sash Width ({unit('length')})
          </FormulaNote>
        </>
      }
      error={error}
      onCalculate={calculate}
      onClear={clearAll}
      result={result.length ? result : null}
      copyFooter="Formula: A = H × W"
      diagram={<DiagramFumehoodFaceArea />}
      outputSlots={{
        duct_height_in: fields.sashHeight ? toImp(fields.sashHeight, 'length') * 12 : undefined,
        duct_width_in: fields.sashWidth ? toImp(fields.sashWidth, 'length') * 12 : undefined,
        area_sqft: fields.faceArea ? toImp(fields.faceArea, 'area') : undefined,
      }}
      onPrefill={(values) => {
        const patch: Partial<Fields> = {};
        if (values.duct_height_in != null && !fields.sashHeight) patch.sashHeight = fromImp(values.duct_height_in / 12, 'length', 2);
        if (values.duct_width_in != null && !fields.sashWidth) patch.sashWidth = fromImp(values.duct_width_in / 12, 'length', 2);
        if (values.area_sqft != null && !fields.faceArea) patch.faceArea = fromImp(values.area_sqft, 'area', 3);
        if (Object.keys(patch).length > 0) set(patch);
      }}
    >
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Sash Height (${unit('length')})`}
          value={fields.sashHeight}
          onChangeText={(v) => set({ sashHeight: v })}
          rightText={unit('length')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Sash Width (${unit('length')})`}
          value={fields.sashWidth}
          onChangeText={(v) => set({ sashWidth: v })}
          rightText={unit('length')}
          maxDecimals={2}
          onSubmitEditing={calculate}
        />
      </View>
      <View style={{ marginBottom: 12 }}>
        <NumericField
          label={`Face Area (${unit('area')})`}
          value={fields.faceArea}
          onChangeText={(v) => set({ faceArea: v })}
          rightText={unit('area')}
          maxDecimals={3}
          onSubmitEditing={calculate}
        />
      </View>
    </CalcShell>
  );
};

export default FumehoodFaceAreaScreen;
